import { StepperOptions, STEPPER_GLOBAL_OPTIONS } from '@angular/cdk/stepper';
import { AfterViewInit, Component, OnDestroy, ViewChild } from '@angular/core';
import { MatStepper } from '@angular/material/stepper';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { SubscriptionList } from '@vendasta/rx-utils';
import { delay, take } from 'rxjs/operators';
import { FacebookAdLeadsService } from '../facebook-ad-leads.service';
import { FacebookLeadAdsFormAutomationConfigurationService } from '../facebook-forms-table/fb-forms-automation-configuration.service';
import { DupeSettingsViewModel, LeadsDuplicateDetectionService } from '../leads-duplicate-detection.service';
import { FacebookIntegrationStepperService } from './facebook-integration-stepper.service';

@Component({
  templateUrl: './facebook-integration-stepper.component.html',
  styleUrls: ['./facebook-integration-stepper.component.scss'],
  providers: [
    {
      provide: STEPPER_GLOBAL_OPTIONS,
      useValue: <StepperOptions>{ showError: true },
    },
    {
      provide: 'SAVE_ON_CHANGES',
      useValue: false,
    },
  ],
  standalone: false,
})
export class FacebookIntegrationStepperComponent implements AfterViewInit, OnDestroy {
  selectedPage$ = this.facebookIntegrationStepperService.selectedPage$;
  subscribedToPage$ = this.facebookIntegrationStepperService.subscribedToPage$.pipe(delay(0));
  isConnectingToPage$ = this.facebookIntegrationStepperService.isConnectingToPage$;
  isDisconnectingPage$ = this.facebookAdLeadsService.isDisconnectingPage$;
  @ViewChild('stepper') private stepper: MatStepper;

  noFormsSelected$ = this.facebookFormsService.noFormsSelected$;
  subscribedToForms$ = this.facebookFormsService.subscribedToForms$;
  isSavingFormsConfig$ = this.facebookFormsService.isSavingFormsConfig$;
  private readonly subscriptions = SubscriptionList.new();

  constructor(
    private readonly facebookIntegrationStepperService: FacebookIntegrationStepperService,
    private readonly duplicateDetectionService: LeadsDuplicateDetectionService,
    private readonly translate: TranslateService,
    private readonly facebookFormsService: FacebookLeadAdsFormAutomationConfigurationService,
    private readonly facebookAdLeadsService: FacebookAdLeadsService,
    private readonly snackbarService: SnackbarService,
    private router: Router,
  ) {}

  ngAfterViewInit(): void {
    this.subscribedToPage$.subscribe((success) => {
      if (success) {
        this.stepper.selected.completed = true;
        this.stepper.next();
      } else {
        this.stepper.selected.completed = false;
      }
    });

    this.subscribedToForms$.subscribe((success) => {
      if (success) {
        this.stepper.selected.completed = true;
        this.stepper.next();
      } else {
        this.stepper.selected.completed = false;
      }
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.destroy();
  }

  updateSelectedPage(pageId: string): void {
    this.facebookIntegrationStepperService.updateSelectedPage(pageId);
  }

  connectSelectedPage(): void {
    this.facebookIntegrationStepperService.connectSelectedPage();
  }

  updateDupeCriteria(setting: DupeSettingsViewModel): void {
    this.duplicateDetectionService.updateCriteria(setting);
  }

  saveCriteria(): void {
    this.duplicateDetectionService
      .saveCriteria()
      .pipe(take(1))
      .subscribe((_) => {
        this.showSuccess('LEAD_INTEGRATIONS.SETTINGS.COMMON.SAVE_SUCCESS');
        this.router.navigate(['facebook-ad-leads']);
      });
  }

  subscribeToForms(): void {
    this.facebookFormsService.updateLeadAdsFormAutomations();
  }

  private showSuccess(textKey: string): void {
    this.snackbarService.successSnack(this.translate.instant(textKey));
  }

  undoChangesFromStep1(): void {
    this.facebookAdLeadsService.DisconnectFromFacebookPage().subscribe(
      (_) => this.stepper.previous(),
      (_) => {
        this.snackbarService.errorSnack(
          this.translate.instant('LEAD_INTEGRATIONS.FACEBOOK.DISCONNECT_UNSUCCESSFULL_MESSAGE'),
        );
      },
    );
  }
}
